import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { MessageEnum } from '../../../core/utils/message.enum';
import { BaseLayoutComponent } from '../../../layouts/base-layout.component';
import { FilterModalComponent } from '../../modal-templates/filter-modal/filter-modal.component';
import { ExecutionCycle } from '../execution-cycle-card/execution-cycle-card.component';
import { ExecutionCycleDetailComponent } from '../execution-cycle-detail/execution-cycle-detail.component';
import { ExecutionCycleViewComponent } from '../execution-cycle-view/execution-cycle-view.component';
import { LatestExecutionViewComponent } from '../latest-execution-view/latest-execution-view.component';
import { ResultSummaryComponent } from '../result-summary/result-summary.component';

@Component({
  selector: "app-execution-overview-view",
  standalone: true,
  imports: [
    CommonModule,
    ExecutionCycleViewComponent,
    LatestExecutionViewComponent,
    ResultSummaryComponent,
    ExecutionCycleDetailComponent,
  ],
  templateUrl: "./execution-overview-view.component.html",
  styleUrls: ["./execution-overview-view.component.scss"],
})
export class ExecutionOverviewViewComponent extends BaseLayoutComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string = '';
  @Input() automationProjectId: string = '';

  executionCycles: ExecutionCycle[] = [];
  allExecutionCycles: ExecutionCycle[] = [];
  isLoading: boolean = false;
  isCyclesLoading: boolean = false;
  activeFilter: { executionCycle: string; testPlan: string; statuses: string[] } | null = null;

  /** Pagination meta from the BE */
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;

  /** Aggregate summary from BE — passed directly to ResultSummaryComponent */
  executionSummary: any = null;
  private readonly modalService = inject(NgbModal);

  /** Controls which panel is shown: the overview list or the per-cycle detail. */
  activeView: 'overview' | 'detail' = 'overview';

  /** Cycle currently shown in the detail view. */
  selectedCycle: ExecutionCycle | null = null;

  private readonly dropdownSub: Subscription = new Subscription();
  private readonly clickSub: Subscription = new Subscription();

  /** Holds the most-recent cycle. Set once on initial load / refresh / delete.
   *  Pagination NEVER updates this — it always reflects the globally latest cycle. */
  latestCycle: ExecutionCycle | undefined = undefined;

  ngOnInit(): void {
    // Resolve projectId from the service if not supplied via @Input
    if (this.projectId) {
      this.tryLoad();
    } else {
      this.projectService.getProjectId().subscribe((id: string | null) => {
        if (id) {
          this.projectId = id;
          this.tryLoad();
        }
      });
    }

    if (!this.automationProjectId) {
      this.projectService
        .getAutomationProjectId()
        .subscribe((id: string | null) => {
          if (id) {
            this.automationProjectId = id;
            this.tryLoad();
          }
        });
    }

    // When the user selects a different cycle from the breadcrumb dropdown, switch the detail view
    this.dropdownSub.add(
      this.breadcrumbService.onDropdownChange().subscribe(({ key, value }) => {
        if (key === 'execution-cycle-detail') {
          const cycle = this.executionCycles.find(c => c.id === value);
          if (cycle) { this.selectedCycle = cycle; }
        }
      })
    );

    // When the user clicks any breadcrumb node while in detail view, return to overview
    this.clickSub.add(
      this.breadcrumbService.onNodeClick().subscribe(() => {
        if (this.activeView === 'detail') {
          this.activeView = 'overview';
          this.selectedCycle = null;
        }
      })
    );
  }

  private tryLoad(): void {
    if (this.projectId && this.automationProjectId) {
      this.loadTopCardData();
      this.loadExecutionCycles();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["projectId"] || changes["automationProjectId"]) &&
      this.projectId &&
      this.automationProjectId
    ) {
      this.currentPage = 1;
      this.loadTopCardData();
      this.loadExecutionCycles();
    }
  }

  ngOnDestroy(): void {
    this.dropdownSub.unsubscribe();
    this.clickSub.unsubscribe();
    this.breadcrumbService.deleteNode({ key: 'execution-cycle-detail', name: '' });
  }

  /**
   * Loads ONLY the top-card data (Latest Execution + Overall Summary).
   * Always fetches page 1. Called on initial load, refresh, and delete.
   * NEVER called during pagination — latestCycle and executionSummary
   * are completely isolated from the cycles list below.
   */
  private loadTopCardData(): void {
    this.isLoading = true;
    this.restService
      .getTestExecutionSummary(
        this.projectId,
        this.automationProjectId,
        { page: 1, size: this.pageSize },
      )
      .subscribe({
        next: (response: any) => {
          const data: ExecutionCycle[] = response?.data ?? [];
          this.latestCycle      = data[0];
          this.executionSummary = response?.summery ?? null;
          this.isLoading        = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Loads ONLY the paginated execution cycles list.
   * Never reads or writes latestCycle / executionSummary.
   */
  loadExecutionCycles(): void {
    if (!this.projectId || !this.automationProjectId) return;

    this.isCyclesLoading = true;
    const payload = { page: this.currentPage, size: this.pageSize };

    this.restService
      .getTestExecutionSummary(
        this.projectId,
        this.automationProjectId,
        payload,
      )
      .subscribe({
        next: (response: any) => {
          this.allExecutionCycles = response?.data ?? [];
          this.executionCycles    = [...this.allExecutionCycles];
          this.totalItems         = response?.meta?.totalItems ?? 0;
          this.isCyclesLoading    = false;
        },
        error: () => {
          this.executionCycles = [];
          this.isCyclesLoading = false;
        },
      });
  }

  handleDeleteSelected(cycleIds: string[]): void {
    if (!cycleIds.length) return;

    this.restService
      .deleteExecutionsList(this.projectId, this.automationProjectId, cycleIds)
      .subscribe({
        next: () => {
          this.toaster.pop({ type: 'success', text: MessageEnum.EXECUTIONS_DELETION_SUCCESS });
          this.currentPage = 1;
          this.loadTopCardData();
          this.loadExecutionCycles();
        },
        error: () => {
          this.toaster.pop({ type: 'error', text: MessageEnum.EXECUTIONS_DELETION_FAILURE });
        },
      });
  }

  handleRefresh(): void {
    this.activeFilter = null;
    this.currentPage = 1;
    this.loadTopCardData();
    this.loadExecutionCycles();
  }

  handleFilter(): void {
    const modalRef = this.modalService.open(FilterModalComponent, {
      windowClass: "filter-window",
      centered: true,
      size: "md",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      title: "Filter",
      saveBtnText: "Apply",
      executionCycles: this.allExecutionCycles,
      previousFilter: this.activeFilter,
    };
    modalRef.result.then(
      (result) => {
        this.activeFilter = result;
        this.applyFilter(result);
      },
      (reason) => {
        if (reason === 'reset') {
          this.activeFilter = null;
          this.executionCycles = [...this.allExecutionCycles];
        }
      },
    );
  }

  applyFilter(filter: { executionCycle: string; testPlan: string; statuses: string[] }): void {
    let filtered = [...this.allExecutionCycles];

    if (filter?.executionCycle) {
      filtered = filtered.filter((c) => c.id === filter.executionCycle);
    }

    if (filter?.testPlan) {
      filtered = filtered
        .map((c) => ({
          ...c,
          children: (c.children ?? []).filter((p: any) => p.id === filter.testPlan),
        }))
        .filter((c) => c.children.length > 0);
    }

    if (filter?.statuses?.length) {
      const statuses = new Set(filter.statuses.map((s) => s.toUpperCase()));
      filtered = filtered
        .map((c) => ({
          ...c,
          children: (c.children ?? []).filter((child: any) =>
            statuses.has(child.status?.toUpperCase())
          ),
        }))
        .filter((c) => c.children.length > 0);
    }

    this.executionCycles = filtered;
  }

  handleViewDetails(cycleId: string): void {
    const cycle = this.executionCycles.find(c => c.id === cycleId);
    if (!cycle) return;

    this.selectedCycle = cycle;
    this.activeView = 'detail';

    // Push a dropdown breadcrumb node for the cycle picker
    this.breadcrumbService.addNode({
      name: cycle.name,
      key: 'execution-cycle-detail',
      clickable: false,
      isDropdown: true,
      dropdownItems: this.executionCycles.map(c => ({ label: c.name, value: c.id })),
      selectedDropdownValue: cycleId,
    });
  }

  handlePageChange(page: number): void {
    this.currentPage = page;
    this.loadExecutionCycles(); // top cards deliberately NOT called here
  }
}
